import { useEffect, useState } from "react";
import { Loader2, MapPin, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { devolverMaterialReserva, getApiErrorMessage } from "@/lib/api";
import { MaterialFoto } from "@/components/material/MaterialShared";

export default function DevolverDialog({ open, onOpenChange, reserva, item, onDone }) {
  const [enSitio, setEnSitio] = useState(false);
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEnSitio(false);
      setNota("");
      setSaving(false);
    }
  }, [open]);

  if (!reserva) return null;
  const ubicacion = item?.ubicacion;

  const submit = async () => {
    setSaving(true);
    try {
      await devolverMaterialReserva(reserva.id, nota);
      toast.success(`${item?.nombre || "Equipo"} devuelto. ¡Gracias!`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo registrar la devolución"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5" /> Devolver material
          </DialogTitle>
          <DialogDescription>Confirma que lo has dejado en su sitio para que el siguiente lo encuentre.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 rounded-xl border p-3">
          <MaterialFoto item={item} className="h-16 w-16 shrink-0 rounded-lg" iconClassName="h-6 w-6" />
          <div className="min-w-0">
            <p className="font-semibold">{item?.nombre}</p>
            {ubicacion ? (
              <p className="mt-1 flex items-center gap-1.5 text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                Devuélvelo a: <span className="font-semibold">{ubicacion}</span>
              </p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">Sin ubicación de guardado definida</p>
            )}
          </div>
        </div>

        {ubicacion && (
          <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-muted/50 px-3 py-2.5 text-sm">
            <Checkbox checked={enSitio} onCheckedChange={(v) => setEnSitio(v === true)} />
            Lo he dejado en <span className="font-medium">{ubicacion}</span>
          </label>
        )}

        <div className="space-y-1">
          <Label htmlFor="mat-nota" className="text-xs">¿Alguna incidencia? (opcional)</Label>
          <Textarea id="mat-nota" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. batería gastada, falta la tapa del objetivo…" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={saving || (ubicacion && !enSitio)} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
            Devolver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
